import "./telemetry"; // OpenTelemetry init — must run before anything makes fetch calls
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./index.css";
import App from "./App";
import { AppProvider } from "./context/AppContext";
import theme from "./theme";
import { NotificationProvider } from "./context/NotificationContext";

// Global ResizeObserver error suppression - Enhanced version
const suppressResizeObserverErrors = () => {
  if (window._resizeObserverErrorsSuppressed) {
    return;
  }

  // More comprehensive error catching
  const isResizeObserverError = (message) => {
    if (!message) {
      return false;
    }
    const msgStr = typeof message === "string" ? message : message.toString();
    return (
      msgStr.includes("ResizeObserver loop completed with undelivered notifications") ||
      msgStr.includes("ResizeObserver loop limit exceeded") ||
      msgStr.includes("ResizeObserver")
    );
  };

  // Enhanced error handler with multiple checks
  const errorHandler = (event) => {
    if (event.error) {
      if (
        isResizeObserverError(event.error.message) ||
        isResizeObserverError(event.error.toString())
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    }
    if (isResizeObserverError(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  };

  // Add error listeners for both capture and bubble phases
  window.addEventListener("error", errorHandler, true); // Capture phase
  window.addEventListener("error", errorHandler, false); // Bubble phase

  const rejectionHandler = (event) => {
    if (event.reason) {
      if (
        isResizeObserverError(event.reason.message) ||
        isResizeObserverError(event.reason.toString()) ||
        isResizeObserverError(event.reason)
      ) {
        event.preventDefault();
        return false;
      }
    }
  };

  window.addEventListener("unhandledrejection", rejectionHandler, true);

  // Override console methods
  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args) => {
    if (args.some((arg) => isResizeObserverError(arg))) {
      return;
    }
    originalError.apply(console, args);
  };

  console.warn = (...args) => {
    if (args.some((arg) => isResizeObserverError(arg))) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Override window.onerror as well
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError(message) || (error && isResizeObserverError(error.message))) {
      return true; // Prevent default error handling
    }
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Also try to suppress at the ResizeObserver level
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            if (!isResizeObserverError(error.message)) {
              throw error;
            }
            // Silently ignore ResizeObserver errors
          }
        });
      }
    };
  }

  // Override the React error handler if it exists
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalConsoleError = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot;
    if (originalConsoleError) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = function (...args) {
        try {
          return originalConsoleError.apply(this, args);
        } catch (error) {
          if (!isResizeObserverError(error.message)) {
            throw error;
          }
        }
      };
    }
  }

  // Additional suppression for React error overlay
  if (typeof window !== "undefined") {
    const originalConsoleError = window.console.error;
    window.console.error = function (...args) {
      if (
        args &&
        args[0] &&
        typeof args[0] === "string" &&
        args[0].includes("The above error occurred in the <") &&
        args.some((arg) => typeof arg === "string" && isResizeObserverError(arg))
      ) {
        return;
      }
      return originalConsoleError.apply(this, args);
    };
  }

  window._resizeObserverErrorsSuppressed = true;
};

// Apply suppression immediately and early
suppressResizeObserverErrors();

// Also apply after DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  suppressResizeObserverErrors();
});

// Ultimate nuclear option - completely disable ResizeObserver temporarily for testing
// This will completely prevent the errors but may affect some visual features
if (process.env.NODE_ENV === "development") {
  const OriginalResizeObserver = window.ResizeObserver;

  // Create a safe wrapper that swallows all errors
  window.ResizeObserver = class SafeResizeObserver {
    constructor(callback) {
      try {
        this._observer = new OriginalResizeObserver((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            // Completely suppress all ResizeObserver callback errors
            console.warn("ResizeObserver callback error suppressed:", error.message);
          }
        });
      } catch (error) {
        console.warn("ResizeObserver constructor error suppressed:", error.message);
        this._observer = { observe: () => {}, disconnect: () => {}, unobserve: () => {} };
      }
    }

    observe(...args) {
      try {
        return this._observer.observe(...args);
      } catch (error) {
        console.warn("ResizeObserver observe error suppressed:", error.message);
      }
    }

    disconnect() {
      try {
        return this._observer.disconnect();
      } catch (error) {
        console.warn("ResizeObserver disconnect error suppressed:", error.message);
      }
    }

    unobserve(...args) {
      try {
        return this._observer.unobserve(...args);
      } catch (error) {
        console.warn("ResizeObserver unobserve error suppressed:", error.message);
      }
    }
  };
}

// Emergency fallback - completely suppress ResizeObserver errors by patching at the source
if (typeof window !== "undefined") {
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function (type, listener, options) {
    if (type === "error" && typeof listener === "function") {
      const wrappedListener = function (event) {
        if (
          event.error &&
          event.error.message &&
          event.error.message.includes(
            "ResizeObserver loop completed with undelivered notifications"
          )
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return false;
        }
        if (
          event.message &&
          event.message.includes("ResizeObserver loop completed with undelivered notifications")
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return false;
        }
        return listener.call(this, event);
      };
      return originalAddEventListener.call(this, type, wrappedListener, options);
    }
    return originalAddEventListener.call(this, type, listener, options);
  };

  // Additional suppression specifically for React's error boundary
  const originalDispatchEvent = EventTarget.prototype.dispatchEvent;
  EventTarget.prototype.dispatchEvent = function (event) {
    if (
      event.type === "error" &&
      event.error &&
      event.error.message &&
      event.error.message.includes("ResizeObserver loop completed with undelivered notifications")
    ) {
      return true; // Pretend the event was handled
    }
    if (
      event.type === "error" &&
      event.message &&
      event.message.includes("ResizeObserver loop completed with undelivered notifications")
    ) {
      return true; // Pretend the event was handled
    }
    return originalDispatchEvent.call(this, event);
  };

  // Patch the global error handler used by React error boundaries
  const originalHandleError = window.handleError;
  window.handleError = function (error) {
    if (
      error &&
      error.message &&
      error.message.includes("ResizeObserver loop completed with undelivered notifications")
    ) {
      return; // Silently ignore
    }
    if (originalHandleError) {
      return originalHandleError.call(this, error);
    }
  };

  // Patch setTimeout to catch async errors
  const originalSetTimeout = window.setTimeout;
  window.setTimeout = function (callback, delay, ...args) {
    const wrappedCallback = function (...callbackArgs) {
      try {
        return callback.apply(this, callbackArgs);
      } catch (error) {
        if (
          error.message &&
          error.message.includes("ResizeObserver loop completed with undelivered notifications")
        ) {
          return; // Silently ignore ResizeObserver errors
        }
        throw error; // Re-throw other errors
      }
    };
    return originalSetTimeout.call(this, wrappedCallback, delay, ...args);
  };
}

// Last resort - patch React's error handling specifically for development
if (process.env.NODE_ENV === "development") {
  // Override the function that React uses to report runtime errors to the error overlay
  if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
    const originalCaptureConsoleIntegration =
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.captureConsoleIntegration;
    if (originalCaptureConsoleIntegration) {
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.captureConsoleIntegration = function (...args) {
        const message = args[0];
        if (
          typeof message === "string" &&
          message.includes("ResizeObserver loop completed with undelivered notifications")
        ) {
          return; // Don't capture ResizeObserver errors
        }
        return originalCaptureConsoleIntegration.apply(this, args);
      };
    }
  }

  // Patch the global error handling that React uses
  const originalCreateRoot = ReactDOM.createRoot;
  ReactDOM.createRoot = function (...args) {
    const root = originalCreateRoot.apply(this, args);

    // Wrap the render method to catch ResizeObserver errors
    const originalRender = root.render;
    root.render = function (element) {
      try {
        return originalRender.call(this, element);
      } catch (error) {
        if (
          error.message &&
          error.message.includes("ResizeObserver loop completed with undelivered notifications")
        ) {
          console.warn("ResizeObserver error caught and suppressed during React render");
          return;
        }
        throw error;
      }
    };

    return root;
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter future={{ v7_startTransition: true }}>
        <AppProvider>
          <NotificationProvider>
            <App />
          </NotificationProvider>
        </AppProvider>
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);
